import { DatePipe, DecimalPipe } from "@angular/common";
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, OnInit, ViewChild, inject } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { NgxPaginationModule } from "ngx-pagination";
import { Subscription } from "rxjs";

import { Tickets } from "src/app/shared/model/tickets/tickets.model";
import { TicketsEstatus } from "src/app/utilerias/enums/tickets-estatus.enum";
import { TicketsService } from "src/app/tickets/tickets.service";
import {
  FiltrosReporteTickets,
  aParametros,
  descargarReporteTickets,
  motivoDelError,
  resumenFiltros,
  sugerenciaSinTickets,
} from "../reporte-tickets.util";

/** Una fila de la tabla con sus chips ya resueltos (no se calculan en cada repintado). */
interface FilaReporte {
  ticket: Tickets;
  clasePrioridad: string;
  claseEstatus: string;
  sla: { texto: string; clase: string } | null;
}

/**
 * Vista previa del reporte de tickets: las mismas columnas del Excel,
 * paginadas en el servidor (param4 trae el total real). Recibe una COPIA
 * de los filtros del diálogo y desde aquí también se puede descargar.
 *
 * Sin zone.js: cada respuesta repinta a mano (pintar()). Cambiar de página
 * cancela la consulta anterior para que una respuesta vieja no pise a la
 * nueva.
 */
@Component({
  selector: "app-modal-reporte",
  standalone: true,
  templateUrl: "./modal-reporte.component.html",
  styleUrls: ["../reporte-tickets.compartido.css", "./modal-reporte.component.css"],
  imports: [
    DatePipe,
    DecimalPipe,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    NgxPaginationModule,
  ],
})
export class ModalReporteComponent implements OnInit {
  private readonly ticketsService = inject(TicketsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  // Opcionales: la ruta TICKETS/ModalReporte monta el componente sin diálogo.
  private readonly dialogRef = inject<MatDialogRef<ModalReporteComponent>>(MatDialogRef, { optional: true });
  readonly filtros = inject<FiltrosReporteTickets | null>(MAT_DIALOG_DATA, { optional: true });

  readonly idPaginador = "reporteTicketsVistaPrevia";
  readonly tamanos = [10, 25, 50];

  resumen: string[] = [];
  /** Qué ampliar si no hay tickets (solo los filtros que se eligieron). */
  sugerencia = "";
  filas: FilaReporte[] = [];
  total = 0;
  pagina = 1;
  tamanoPagina = 10;

  cargando = false;
  /** false hasta la primera respuesta: decide entre "cargando" y la tabla. */
  cargado = false;
  errorCarga = "";

  generando = false;
  resultado: "vacio" | "error" | "exito" | null = null;
  detalleError = "";

  @ViewChild("botonDescargar") private botonDescargar?: ElementRef<HTMLButtonElement>;

  private consulta: Subscription | null = null;
  private descarga: Subscription | null = null;
  private destruido = false;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destruido = true;
      this.consulta?.unsubscribe();
      this.descarga?.unsubscribe();
    });
  }

  ngOnInit(): void {
    if (!this.filtros) {
      this.errorCarga = "Abre la vista previa desde el reporte de tickets.";
      return;
    }
    this.resumen = resumenFiltros(this.filtros);
    this.sugerencia = sugerenciaSinTickets(this.filtros);
    this.cargar();
  }

  get desde(): number {
    return this.total ? (this.pagina - 1) * this.tamanoPagina + 1 : 0;
  }

  get hasta(): number {
    return Math.min(this.pagina * this.tamanoPagina, this.total);
  }

  cargar(): void {
    if (!this.filtros) {
      return;
    }
    this.consulta?.unsubscribe();
    const parametros = aParametros(this.filtros);
    parametros.regInicial = (this.pagina - 1) * this.tamanoPagina;
    parametros.regXPagina = this.tamanoPagina;

    // Sin pintar aquí: se llega desde un clic (Angular repinta al salir del
    // manejador) o desde ngOnInit, y el repintado manual va en las respuestas.
    this.cargando = true;
    this.errorCarga = "";

    this.consulta = this.ticketsService.getDatosReporteReporte(parametros).subscribe({
      next: (respuesta) => {
        const lista = Array.isArray(respuesta.list) ? respuesta.list : [];
        const total = Math.max(0, Math.trunc(Number(respuesta.param4) || 0));
        // Si el total cambió y la página quedó fuera, se va a la última.
        const paginas = Math.ceil(total / this.tamanoPagina);
        if (paginas > 0 && this.pagina > paginas) {
          this.pagina = paginas;
          this.cargar();
          return;
        }
        this.filas = lista.map((ticket) => this.aFila(ticket));
        // Si el conteo llegara corto, al menos cuenta las filas que sí vinieron.
        this.total = lista.length ? Math.max(total, (this.pagina - 1) * this.tamanoPagina + lista.length) : total;
        if (!this.total) {
          this.pagina = 1;
        }
        this.cargando = false;
        this.cargado = true;
        this.pintar();
      },
      error: (error) => {
        this.cargando = false;
        this.errorCarga = motivoDelError(error, "Revisa tu conexión e intenta de nuevo.");
        this.pintar();
      },
    });
  }

  irAPagina(pagina: number): void {
    if (pagina === this.pagina) {
      return;
    }
    this.pagina = pagina;
    this.cargar();
  }

  cambiarTamano(valor: string): void {
    const tamano = Number(valor);
    if (!this.tamanos.includes(tamano) || tamano === this.tamanoPagina) {
      return;
    }
    this.tamanoPagina = tamano;
    this.pagina = 1;
    this.cargar();
  }

  descargar(): void {
    if (!this.filtros || this.generando) {
      return;
    }
    this.resultado = null;
    this.ponerGenerando(true);

    this.descarga = descargarReporteTickets(this.ticketsService, this.filtros).subscribe({
      next: (respuesta) => {
        this.ponerGenerando(false);
        this.resultado = respuesta.tipo === "archivo" ? "exito" : "vacio";
        this.pintar();
        this.devolverFoco();
      },
      error: (error) => {
        this.ponerGenerando(false);
        this.resultado = "error";
        this.detalleError = motivoDelError(error, "Revisa tu conexión e intenta de nuevo.");
        this.pintar();
        this.devolverFoco();
      },
    });
  }

  cerrar(): void {
    if (!this.generando) {
      this.dialogRef?.close();
    }
  }

  // ------------------------------------------------------------------

  private aFila(ticket: Tickets): FilaReporte {
    return {
      ticket,
      clasePrioridad: clasePrioridad(ticket.prioridad),
      claseEstatus: claseEstatus(ticket.id_estatus),
      sla: sla(ticket.cumpleSla),
    };
  }

  /** Mientras se genera no se cierra: ni Escape, ni clic fuera, ni Cerrar. */
  private ponerGenerando(generando: boolean): void {
    this.generando = generando;
    if (this.dialogRef) {
      this.dialogRef.disableClose = generando;
    }
  }

  private pintar(): void {
    if (this.destruido) {
      return;
    }
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  /**
   * El botón de descargar se deshabilita mientras se genera y el foco se
   * va a <body>. Ya repintado, regresa a Descargar si nadie más lo tomó
   * (mismo caso que en el diálogo de filtros).
   */
  private devolverFoco(): void {
    const boton = this.botonDescargar?.nativeElement;
    const activo = document.activeElement;
    if (!this.destruido && boton && !boton.disabled && (!activo || activo === document.body)) {
      boton.focus();
    }
  }
}

/** Sin acentos y en minúsculas, para comparar lo que escribió cada catálogo. */
function normal(valor: Tickets["prioridad"] | Tickets["cumpleSla"]): string {
  return String(valor ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();
}

/** Las prioridades son un catálogo libre: se reconocen por nombre y el resto va neutro. */
function clasePrioridad(prioridad: Tickets["prioridad"]): string {
  const valor = normal(prioridad);
  if (valor.startsWith("alta") || valor.includes("critic") || valor.includes("urgen")) {
    return "is-danger";
  }
  if (valor.startsWith("media")) {
    return "is-pending";
  }
  return "is-inactive";
}

function claseEstatus(estatus: unknown): string {
  switch (Number(estatus)) {
    case TicketsEstatus.ATENDIDA:
    case TicketsEstatus.VOBO:
      return "is-active";
    case TicketsEstatus.CANCELADA:
      return "is-inactive";
    default:
      return "is-pending";
  }
}

function sla(valor: Tickets["cumpleSla"]): { texto: string; clase: string } | null {
  const texto = normal(valor);
  if (texto === "si") {
    return { texto: "Sí", clase: "is-active" };
  }
  if (texto === "no") {
    return { texto: "No", clase: "is-danger" };
  }
  return null;
}
